import { GroupAddOutlined, Home } from "@mui/icons-material";
import { Breadcrumbs, CardContent, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Link, Stack, Typography } from "@mui/material";
import BreadcrumbLink from "components/breadcrumbs/BreadcrumbLink";
import NewCommunityDialog from "components/dialogs/communities/NewCommunityDialog";
import NewCommunityMemberConfigureInstallationDialog from "components/dialogs/communities/NewCommunityMemberConfigureInstallationDialog";
import NewCommunityMemberConfigureMembersDialog from "components/dialogs/communities/NewCommunityMemberConfigureMembersDialog";
import NewCommunityMemberConfigureTariffPlanDialog from "components/dialogs/communities/NewCommunityMemberConfigureTariffPlanDialog";
import { NewCommunityStatusTableFilter } from "components/forms/communities/CommunityStatusSelect";
import { CommunityTypeTableFilter } from "components/forms/communities/CommunityTypeSelector";
import IndieCancelButton from "components/forms/IndieCancelButton";
import IndieContinueButton from "components/forms/IndieContinueButton";
import IndieNewItemButton from "components/forms/IndieNewItemButton";
import IndieCard from "components/IndieCard";
import { H3, Small } from "components/Typography";
import { ClientsContextProvider } from "contexts/ClientsContext";
import { CommunityContextProvider } from "contexts/communities/CommunityContext";
import useAuth from "hooks/useAuth";
import ApplicationList from "page-sections/common/ApplicationList";
import CommunityCard from "page-sections/communities/communities/card/CommunityCard";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useCommunitiesAPI } from "service/CommunitiesAPIService";

const AgentCommunitiesList = () => {

    const { t } = useTranslation();
    const api = useCommunitiesAPI();
    const navigate = useNavigate();
    const { hasPermissionGroup } = useAuth();

    const [openNewCommunityDialog, setOpenNewCommunityDialog] = useState(false);
    const [openNewCommunityMemberDialog, setOpenNewCommunityMemberDialog] = useState(false);
    const [openConfigureInstallationMembersDialog, setOpenConfigureInstallationMembersDialog] = useState(false);
    
    const [openConfirmContinueToMemberConfigurationDialog, setOpenConfirmContinueToMemberConfigurationDialog] = useState(false);
    const [openConfirmContinueToTariffPlanConfigurationDialog, setOpenConfirmContinueToTariffPlanConfigurationDialog] = useState(false);
    const [openConfigureTariffPlanDialog, setOpenConfigureTariffPlanDialog] = useState(false);

    const [createdInstallation, setCreatedInstallation] = useState(null);
    const [createdMember, setCreatedMember] = useState(null);

    const handleNavigation = (community) => {
        navigate(`/communities/${community.id}/`);
    }

    const columns = useMemo(() => [
        {
            accessor: 'dso_id',
            Header: t("label.table.header.communityNumber"),
            Cell: ({ value, row }) => (
                <Link href={`/communities/${row.original.id}/energy`}><Small>{`${value} / ${row.original.type.toUpperCase()}`}</Small></Link>
            ),
        },
        {
            accessor: 'type',
            Header: t("label.table.header.communityType"),
            Cell: ({ value }) => <Small>{t(`label.community.type.${value}`)}</Small>,
            filterComponent: CommunityTypeTableFilter
        },
        {
            accessor: 'aggregator_deliverypoint',
            Header: t("aggregatorDeliverypoint"),
        },
        {
            accessor: 'agent_entity.name',
            Header: t("label.step.newProducer.agentEntityStep")
        },
        {
            accessor: 'status',
            Header: t("label.table.header.status"),
            Cell: ({ value }) => <Small>{t(`label.community.status.${value}`)}</Small>,
            filterComponent: NewCommunityStatusTableFilter
        },
    ], [t]);

    const onSuccessCreateInstallation = (installation) => {
        setCreatedInstallation(installation);
        setOpenNewCommunityMemberDialog(false);
        setOpenConfirmContinueToMemberConfigurationDialog(true);
    }

    const onCancelContinueToConfigureMember = () => {
        setCreatedInstallation(null);
        setOpenConfirmContinueToMemberConfigurationDialog(false);
    }

    const onConfirmContinueToConfigureMember = () => {
        setOpenConfirmContinueToMemberConfigurationDialog(false);
        setOpenConfigureInstallationMembersDialog(true);
    }

    const onSuccessConfigureMembers = (member) => {
        setCreatedMember(member);
        setOpenConfigureInstallationMembersDialog(false);
        setOpenConfirmContinueToTariffPlanConfigurationDialog(true);
    }

    const onCancelContinueToConfigureTariffPlan = () => {
        setCreatedMember(null);
        setOpenConfirmContinueToTariffPlanConfigurationDialog(false);
    }

    const onConfirmContinueToConfigureTariffPlan = () => {
        setOpenConfirmContinueToTariffPlanConfigurationDialog(false);
        setOpenConfigureTariffPlanDialog(true);
    }

    const onSuccessConfigureTariffPlan = () => {
        setOpenConfigureTariffPlanDialog(false);
        setCreatedMember(null);
        setCreatedInstallation(null);
    }

    const onCloseCommunityCreation = () => {
        setOpenNewCommunityDialog(false);
    }

    const onSuccessCommunityCreation = (data) => {
        onCloseCommunityCreation();
        handleNavigation(data);
    };

    const getBusinessType = () => {
        const hasAggregation = hasPermissionGroup("ENERGY_AGENT_AGGRTN_USER");
        const hasCommunities = hasPermissionGroup("ENERGY_AGENT_EGAC_USER");

        if (hasAggregation && !hasCommunities) return "aggregation";
        if (hasCommunities && !hasAggregation) return "communities";
        if (hasAggregation && hasCommunities) return "";

        return "";
    }

    return (
        <Stack spacing={2}>
            <IndieCard>
                <CardContent>
                    <Stack direction="column" spacing={1}>
                        <Breadcrumbs>
                            <BreadcrumbLink value={"label.menu.home"} href="/" icon={<Home sx={{ mr: 0.5 }} fontSize="inherit" />}/>
                            <BreadcrumbLink value={"label.menu.communities"} />
                        </Breadcrumbs>
                        <H3>{t("label.menu.communities")}</H3>
                    </Stack>
                </CardContent>
            </IndieCard>
            <IndieCard>
                <CardContent>
                    <ApplicationList
                        id="communities_list"
                        application={"communities"}
                        dataFetcher={api.list}
                        showAppBar={true}
                        columns={columns}
                        showGridView
                        cardComponent={CommunityCard}
                        outlined
                        showSearch
                        appBar={[
                            <IndieNewItemButton key={"new"} id="create_community_button" variant="contained" onClick={() => setOpenNewCommunityDialog(true)} label={t("label.menu.addCommunity")}/>,
                            hasPermissionGroup("ENERGY_AGENT_EGAC_USER") && <IndieNewItemButton key={"newMember"} id="create_community_member_button"  variant="outlined" onClick={() => setOpenNewCommunityMemberDialog(true)} label={t("label.button.newMember")}/>
                        ]}
                    />
                </CardContent>
            </IndieCard>

            <ClientsContextProvider>
                <CommunityContextProvider>
                    <NewCommunityMemberConfigureInstallationDialog open={openNewCommunityMemberDialog} onClose={() => setOpenNewCommunityMemberDialog(false)} onSuccess={onSuccessCreateInstallation} />
                    <ConfirmContinueToMemberConfigurationDialog open={openConfirmContinueToMemberConfigurationDialog} onConfirm={onConfirmContinueToConfigureMember} onClose={onCancelContinueToConfigureMember} />
                    <NewCommunityMemberConfigureMembersDialog open={openConfigureInstallationMembersDialog} onClose={() => setOpenConfigureInstallationMembersDialog(false)} installation={createdInstallation} onSuccess={onSuccessConfigureMembers} />
                    <ConfirmContinueToTariffPlanConfigurationDialog open={openConfirmContinueToTariffPlanConfigurationDialog} onConfirm={onConfirmContinueToConfigureTariffPlan} onClose={onCancelContinueToConfigureTariffPlan} />
                    <NewCommunityMemberConfigureTariffPlanDialog open={openConfigureTariffPlanDialog} onClose={() => setOpenConfigureTariffPlanDialog(false)} initialMember={createdMember} handleSuccess={onSuccessConfigureTariffPlan} />
                </CommunityContextProvider>

                {openNewCommunityDialog && (
                    <NewCommunityDialog 
                        open={openNewCommunityDialog} 
                        handleClose={onCloseCommunityCreation}
                        handleSuccess={onSuccessCommunityCreation}
                        businessType={getBusinessType}
                    />
                )}
            </ClientsContextProvider>
        </Stack>
    )
};

const ConfirmContinueToMemberConfigurationDialog = ({open, onConfirm, onClose}) => {

    const { t } = useTranslation();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Stack spacing={2} direction="row">
                    <GroupAddOutlined color="primary" />
                    <Typography variant="subtitle1" color="textPrimary">{t("label.button.newMember")}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Divider />
                    <DialogContentText>{t("label.dialog.confirmContinueToMemberConfiguration.message")}</DialogContentText>
                </Stack>
            </DialogContent>
            <DialogActions>
                <IndieCancelButton onClick={onClose} />
                <IndieContinueButton onClick={onConfirm} autoFocus />
            </DialogActions>
        </Dialog>
    )
}

const ConfirmContinueToTariffPlanConfigurationDialog = ({open, onConfirm, onClose}) => {

    const { t } = useTranslation();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Stack spacing={2} direction="row">
                    <GroupAddOutlined color="primary" />
                    <Typography variant="subtitle1" color="textPrimary">{t("label.title.configureTariffPlan")}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Divider />
                    <DialogContentText>{t("label.dialog.confirmContinueToTariffPlanConfigurationDialog.message")}</DialogContentText>
                </Stack>
            </DialogContent>
            <DialogActions>
                <IndieCancelButton onClick={onClose} />
                <IndieContinueButton onClick={onConfirm} autoFocus />
            </DialogActions>
        </Dialog>
    )
}

export default AgentCommunitiesList;